import React, { useState } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  LocalPharmacy,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  HealthAndSafety,
  Verified,
  Speed,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types/auth';

const LoginForm: React.FC = () => {
  const { state, login, clearError } = useAuth();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    if (state.error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) return;
    try {
      await login(credentials);
    } catch (_) {
      // Error is handled by AuthContext
    }
  };

  const features = [
    { icon: <HealthAndSafety sx={{ fontSize: 20 }} />, text: 'ACP Standards Compliant' },
    { icon: <Verified sx={{ fontSize: 20 }} />, text: 'Secure & HIPAA-Aligned' },
    { icon: <Speed sx={{ fontSize: 20 }} />, text: 'Streamlined Workflows' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#F0F4F8',
      }}
    >
      {/* Left branding panel */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 45%',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          background: 'linear-gradient(145deg, #0A1929 0%, #003F8A 60%, #005EB8 100%)',
          px: 7,
          py: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            width: 400,
            height: 400,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.06)',
            top: -100,
            right: -180,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 280,
            height: 280,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.08)',
            bottom: 60,
            right: -80,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 160,
            height: 160,
            borderRadius: '50%',
            bgcolor: 'rgba(59,130,196,0.12)',
            bottom: 180,
            left: -40,
          }}
        />

        {/* Logo */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '14px',
            bgcolor: 'rgba(255,255,255,0.12)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <LocalPharmacy sx={{ fontSize: 30, color: 'white' }} />
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: 'white',
            fontWeight: 800,
            lineHeight: 1.1,
            mb: 1.5,
            fontSize: '2.4rem',
          }}
        >
          Alberta Pharmacy
          <br />
          <Box component="span" sx={{ color: '#60A5FA' }}>
            Assistant
          </Box>
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.6)',
            mb: 5,
            maxWidth: 340,
            lineHeight: 1.7,
            fontSize: '0.95rem',
          }}
        >
          A professional platform built to support Alberta pharmacists and pharmacy
          technicians with prescription adaptations, clinical documentation, and 
          ACP compliance workflows.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {features.map((f, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#60A5FA',
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                {f.text}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 'auto', pt: 6 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
            © 2026 Alberta Pharmacy Assistant Platform. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                bgcolor: '#005EB8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalPharmacy sx={{ fontSize: 24, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary">AB Pharmacy</Typography>
              <Typography variant="caption" color="text.secondary">Assistant Platform</Typography>
            </Box>
          </Box>

          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, color: 'text.primary' }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Sign in to your account to continue
          </Typography>

          {state.error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
              {state.error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: 'text.primary' }}>
                Email address
              </Typography>
              <TextField
                fullWidth
                name="username"
                type="email"
                placeholder="you@rxpharmacy.ab.ca"
                value={credentials.username}
                onChange={handleChange}
                required
                disabled={state.isLoading}
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: 'text.primary' }}>
                Password
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={credentials.password}
                onChange={handleChange}
                required
                disabled={state.isLoading}
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={state.isLoading || !credentials.username || !credentials.password}
              sx={{
                mt: 0.5,
                py: 1.5,
                fontWeight: 700,
                fontSize: '0.95rem',
                background: 'linear-gradient(90deg, #005EB8 0%, #003F8A 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #003F8A 0%, #002060 100%)',
                },
              }}
            >
              {state.isLoading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Sign In'}
            </Button>
          </Box>

          <Box
            sx={{
              mt: 4,
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(0,94,184,0.05)',
              border: '1px solid rgba(0,94,184,0.12)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
              Demo credentials
            </Typography>
            {[
              { role: 'Admin', email: 'admin@rxpharmacy.ab.ca', pw: 'admin123' },
              { role: 'Pharmacist', email: 'pharmacist@rxpharmacy.ab.ca', pw: 'pharma123' },
              { role: 'Technician', email: 'tech@rxpharmacy.ab.ca', pw: 'tech123' },
            ].map(({ role, email, pw }) => (
              <Box
                key={role}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, cursor: 'pointer' }}
                onClick={() => setCredentials({ username: email, password: pw })}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', minWidth: 80 }}>
                  {role}:
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {email}
                </Typography>
              </Box>
            ))}
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
              Click a row to auto-fill credentials
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginForm;
